import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
    import { supabase } from '@/lib/supabaseClient';
    import { useToast } from '@/components/ui/use-toast';

    const AuthContext = createContext(undefined);

    export const AuthProvider = ({ children }) => {
      const { toast } = useToast();

      const [user, setUser] = useState(null);
      const [session, setSession] = useState(null);
      const [loading, setLoading] = useState(true);

      const handleSession = useCallback(async (session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }, []);

      useEffect(() => {
        const getSession = async () => {
          const { data: { session } } = await supabase.auth.getSession();
          handleSession(session);
        };

        getSession();

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            handleSession(session);
          }
        );

        return () => subscription.unsubscribe();
      }, [handleSession]);

      const signUp = useCallback(async (email, password, options) => {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options,
        });

        if (error) {
          toast({
            variant: "destructive",
            title: "Sign up Failed",
            description: error.message || "Something went wrong",
          });
        }

        return { error };
      }, [toast]);

      const signIn = useCallback(async (email, password) => {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          toast({
            variant: "destructive",
            title: "Sign in Failed",
            description: error.message || "Something went wrong",
          });
        }

        return { error };
      }, [toast]);

      const signOut = useCallback(async () => {
        // Local scope avoids the server round-trip that throws
        // "Auth session missing" when there's no live session (installed PWA).
        try {
          await supabase.auth.signOut({ scope: 'local' });
        } catch (_e) {
          /* already signed out */
        }
        try { window.localStorage.removeItem('parcelink_session_started_at'); } catch (_e) {}
        return { error: null };
      }, []);

      const value = useMemo(() => ({
        user,
        session,
        loading,
        signUp,
        signIn,
        signOut,
      }), [user, session, loading, signUp, signIn, signOut]);

      return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
    };

    export const useAuth = () => {
      const context = useContext(AuthContext);
      if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
      }
      return context;
    };